import type { PaymentHistoryViewDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import {
  CustomerStates,
  OVERDUE_CHARGE_STATUS,
  PaymentMethodTypes,
  SubscriptionStatuses,
} from '@subscriptions/domain/types/SubscriptionsConst';
import { getShortDateLabel } from '@subscriptions/presentation/utils/shortDate';

/** A row the view wrote itself, which always knows the block it settles. */
export type OverdueChargeRow = PaymentHistoryViewDto & { blockUuid: string };

/**
 * Builds the history row for the charge a subscription owes, or null when it owes nothing.
 *
 * Only SPEI needs it. A declined card leaves a failed payment that the history already lists,
 * while a transfer that never arrives leaves nothing at all.
 *
 * A churned subscription is left out: its charge belongs to a closed cycle and what it offers
 * is reactivation, not settling the month.
 *
 * @param block - Subscription block as the detail loaded it
 * @param concept - Localized concept, the same one the settled charges carry
 * @returns The row to put above the paid ones, or null
 */
export const buildOverdueChargeRow = (block: SubscriptionBlockViewDto, concept: string): OverdueChargeRow | null => {
  const isSpei = block.paymentMethod?.methodType === PaymentMethodTypes.SPEI;
  const isChurned = block.customerState === CustomerStates.INACTIVE;

  if (block.status !== SubscriptionStatuses.PAYMENT_REQUIRED || !isSpei || isChurned) {
    return null;
  }

  const paymentDate = getShortDateLabel(block.paymentDate);

  if (!paymentDate) {
    return null;
  }

  return {
    uuid: `overdue:${block.uuid}`,
    paymentUuid: `overdue:${block.uuid}`,
    paymentDate,
    createdAt: paymentDate,
    subscription: block.name,
    concept,
    status: OVERDUE_CHARGE_STATUS,
    paymentMethod: PaymentMethodTypes.SPEI,
    paymentMethodDetail: '',
    subscriptionAmount: formatCurrencyMXN(block.total),
    license: '',
    folios: '',
    financing: '',
    discount: '',
    folio: '',
    pdfUrl: null,
    xmlUrl: null,
    invoiceStatus: null,
    invoiceDataUuid: null,
    blockUuid: block.uuid,
    errorMessage: null,
    isOverdueCharge: true,
  };
};

/**
 * Builds the owed rows of a whole organization, oldest debt first.
 *
 * @param blocks - Subscription blocks of the organization
 * @param concept - Localized concept, the same one the settled charges carry
 * @returns The rows to put above the paid ones
 */
export const buildOverdueChargeRows = (blocks: SubscriptionBlockViewDto[] = [], concept: string): OverdueChargeRow[] =>
  [...blocks]
    .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate))
    .map((block) => buildOverdueChargeRow(block, concept))
    .filter((row): row is OverdueChargeRow => row !== null);
